import { Linkable } from '@/components/blocks/Linkable';
import { TableCellProductImage } from '@/components/blocks/ProductImage';
import { ProgressIndicator } from '@/components/blocks/ProgressIndicator';
import { ItemPaint } from '@/components/content/OrderItemTable/parts/ItemPaint';
import { ItemPaintDetails } from '@/components/content/OrderItemTable/parts/ItemPaintDetails';
import { OrderItemQuantity } from '@/components/content/OrderItemTable/parts/Quantity';
import { OrderItemTableRowData } from '@/components/content/OrderItemTable/parts/Table';
import { OrderItemUnitPrice } from '@/components/content/OrderItemTable/parts/UnitPrice';
import {
	orderItemTableItemDetailsMiniPriceSX,
	orderItemTableItemDetailsMiniSX,
} from '@/components/content/OrderItemTable/styles/orderItemTableItemDetailsMini';
import { processRowPaints } from '@/components/content/OrderItemTable/utils/utils';
import { useOrderItemTableRow } from '@/data/Content/OrderItemTable';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { usePaintColors } from '@/data/Content/usePaintColors';
import { useLocalization } from '@/data/Localization';
import { ContentContext } from '@/data/context/content';
import { replaceImagePath } from '@/utils/hdmPathConverter';
import { parseHTML } from '@/utils/parseHTMLModern';
import { Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useContext, useEffect, useMemo, useState } from 'react';
import { OpenSansFontWeights } from 'shared/custom/constants/commonConstants';

type Props = {
	gtmSelectItem?: () => void;
};

// eslint-disable-next-line complexity
export const OrderItemItemDetailsMini: React.FC<Props> = ({ gtmSelectItem }) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const {
		details,
		freeGift,
		itemDetails,
		quantity: { unitQuantity },
	} = useContext(ContentContext) as OrderItemTableRowData & ReturnType<typeof useOrderItemTableRow>;
	const { name, thumbnail, href, prices, loading, partNumber, manufacturer } = details;
	const labels = useLocalization('OrderItemTable').Labels;
	const CartLabels = useLocalization('MiniCart');
	const ProductLabels = useLocalization('HDMProductDetail');
	const router = useNextRouter();
	const checkoutRoute = useLocalization('Routes').CheckOut.route.t();
	const nominalQuantity = Number(unitQuantity);
	const rowPaintString = itemDetails?.colorCode ? itemDetails?.colorCode : '';
	const rowPaintArray = useMemo(() => rowPaintString.split(','), [rowPaintString]);
	const { colorClassification } = usePaintColors();
	const { allLineColors = [], allPaintLines = [] } = colorClassification ?? {};
	const [rowPaints, setRowPaints] = useState<any>([]);
	const pathname = router.asPath.split('?').at(0);
	const check = new RegExp(`\\b${checkoutRoute}$`);
	const inCheckout = !!pathname?.match(check);
	const thumbnailSrc = thumbnail ? replaceImagePath(thumbnail) : '';
	const nameFormatted = name.charAt(0) + name.slice(1).toLowerCase();
	const manufacturerFormatted = manufacturer.charAt(0) + manufacturer.slice(1).toLowerCase();
	const minimalQuantityMessage =
		nominalQuantity > 1
			? `${ProductLabels.CartOrderMinimalQuantity.t()}  ${nominalQuantity}`
			: null;
	const secondaryUnitPriceMessage = prices?.wholeUnit
		? `${ProductLabels.Price.t()}  ${prices.wholeUnit.toLowerCase()}`
		: null;
	const additionalMessage = minimalQuantityMessage || secondaryUnitPriceMessage;

	useEffect(() => {
		if (allPaintLines?.length === 0) return;
		const processed = processRowPaints(rowPaintArray, allPaintLines, allLineColors, manufacturer);
		setRowPaints(processed);
	}, [allPaintLines, allLineColors, rowPaintString, manufacturer, rowPaintArray]);

	if (loading) {
		return <ProgressIndicator />;
	}

	return (
		<Stack direction="row" alignItems="flex-start" sx={orderItemTableItemDetailsMiniSX.container}>
			{thumbnail ? (
				<Linkable href={href} id={href} data-testid={href} className="quick-cart__product-image">
					<TableCellProductImage
						{...{
							src: thumbnailSrc,
							alt: `${labels.ProductThumbnail.t()} of ${name}`,
							isThumbnail: true,
							sx: orderItemTableItemDetailsMiniSX.image,
						}}
						gtmSelectItem={gtmSelectItem}
					/>
				</Linkable>
			) : null}
			<Stack
				direction="column"
				alignItems="flex-start"
				sx={orderItemTableItemDetailsMiniSX.details}
			>
				<Linkable href={href} id={href} data-testid={href} onClick={gtmSelectItem}>
					<Typography variant="bodySubText" sx={orderItemTableItemDetailsMiniSX.name}>
						<span
							style={{ fontWeight: OpenSansFontWeights.semibold }}
							className="quick-cart__product-brand"
						>
							{manufacturerFormatted && parseHTML(manufacturerFormatted)}
						</span>{' '}
						<span className="quick-cart__product-name">
							{nameFormatted && parseHTML(nameFormatted)}
						</span>
					</Typography>
				</Linkable>
				{rowPaintString !== '' ? <ItemPaint rowPaints={rowPaints} /> : null}
				<Stack direction="row" spacing={1.25}>
					<Typography
						variant="bodySubText"
						sx={orderItemTableItemDetailsMiniSX.sku}
						className="quick-cart__product-sku"
					>
						{`${ProductLabels.SKU.t()} ${partNumber}`}
					</Typography>
					{additionalMessage ? (
						<Typography variant="bodySubText" sx={orderItemTableItemDetailsMiniSX.sku}>
							{additionalMessage}
						</Typography>
					) : null}
				</Stack>
				{rowPaintString === '' ? (
					<Stack
						direction={isMobile ? 'column' : 'row'}
						sx={orderItemTableItemDetailsMiniSX.details}
						justifyContent="space-between"
						alignItems="center"
					>
						<OrderItemQuantity mini readOnly={inCheckout || freeGift} />
						{prices ? (
							<Stack
								direction="row"
								sx={orderItemTableItemDetailsMiniPriceSX(isMobile)}
								alignItems="center"
								className="quick-cart__product-price"
							>
								{isMobile ? (
									<Typography variant="bodyCaption" sx={{ color: 'grey.200' }}>
										{CartLabels.SubtotalLabel.t()}
									</Typography>
								) : null}
								<OrderItemUnitPrice />
							</Stack>
						) : null}
					</Stack>
				) : (
					<ItemPaintDetails />
				)}
			</Stack>
		</Stack>
	);
};
